'use client';

import { getParameterSpec, getUnitLabel, UNIT_KEY_LABELS, VALID_UNITS, VALUE_KEY_LABELS } from '../lib/unit-conversion';

import { FormLabel } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { useTranslation } from 'react-i18next';
interface ActivityFormQuantityInputsProps {
  activityMethod: string;
  methodValues: Record<string, string>;
  methodUnits: Record<string, string>;
  onValueChange: (key: string, value: string) => void;
  onUnitChange: (key: string, value: string) => void;
  disabled?: boolean;
  error?: string;
}

export function ActivityFormQuantityInputs({
  activityMethod,
  methodValues,
  methodUnits,
  onValueChange,
  onUnitChange,
  disabled,
  error,
}: ActivityFormQuantityInputsProps) {
  const { t } = useTranslation('ghg');
  if (!activityMethod) return null;

  const spec = getParameterSpec(activityMethod);

  return (
    <div className="space-y-3" data-testid="quantity-inputs">
      <FormLabel>How much?</FormLabel>

      <div className="grid gap-3 sm:grid-cols-2">
        {spec.requiredValueKeys.map((valueKey) => (
          <div key={valueKey} className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">
              {VALUE_KEY_LABELS[valueKey] ?? valueKey}
            </label>
            <Input
              type="number"
              step="any"
              min="0"
              value={methodValues[valueKey] ?? ''}
              onChange={(e) => { onValueChange(valueKey, e.target.value); }}
              onBlur={(e) => {
                const val = Number(e.target.value);
                if (Number.isFinite(val) && val < 0) {
                  onValueChange(valueKey, '0');
                }
              }}
              placeholder={`Enter ${(VALUE_KEY_LABELS[valueKey] ?? valueKey).toLowerCase()}`}
              disabled={disabled}
              data-testid={`quantity-input-${valueKey}`}
            />
          </div>
        ))}

        {spec.requiredUnitKeys.map((unitKey) => {
          const unitOptions = VALID_UNITS[unitKey] ?? [];
          return (
            <div key={unitKey} className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">
                {UNIT_KEY_LABELS[unitKey] ?? unitKey}
              </label>
              {unitOptions.length > 0 ? (
                <Select
                  options={unitOptions.map((unit) => ({
                    value: unit,
                    label: getUnitLabel(unit),
                  }))}
                  value={methodUnits[unitKey] ?? ''}
                  onChange={(value) => { onUnitChange(unitKey, value); }}
                  placeholder={t('placeholder.selectUnit3')}
                  disabled={disabled}
                  data-testid={`unit-select-${unitKey}`}
                />
              ) : (
                <Input
                  value={methodUnits[unitKey] ?? ''}
                  onChange={(e) => { onUnitChange(unitKey, e.target.value); }}
                  placeholder={`Enter ${(UNIT_KEY_LABELS[unitKey] ?? unitKey).toLowerCase()}`}
                  disabled={disabled}
                  data-testid={`unit-input-${unitKey}`}
                />
              )}
            </div>
          );
        })}
      </div>

      {error && (
        <p className="text-sm text-destructive" data-testid="quantity-error">
          {error}
        </p>
      )}
    </div>
  );
}
